import { FC, ReactElement, useCallback, useEffect, useState } from 'react';

import { getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setDrawerOpen } from 'store/drawerSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaExpandablePreview } from 'raa-react';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import { GetUtkastAction } from 'components/aktivitetPresentation/GetUtkastAction';
import PubliceradeBebyggelseobjekt from 'components/aktivitetPresentation/publiceradeBebyggelseobjekt/PubliceradeBebyggelseobjekt';
import { DrawerBody, DrawerSection } from 'components/drawerSections/DrawerSections';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import {
  replaceBebyggelseobjektFeaturesInActiveSource,
  replaceInventeringInActiveSource,
  replaceUtkastFeaturesInActiveSource
} from 'components/map/Layers';
import { Colors } from 'components/map/Styles';
import { inventeringCircle } from 'components/map/icons';
import ShareButton from 'components/share/ShareButton';
import { TextButton } from 'components/sharedElements/sharedElements';
import UtkastList from 'components/utkastList/UtkastList';

import { AktivitetStatusTyp, BebyggelseobjektUtkastIndex, GeografiskIndelning } from 'models';

import { AktivitetStatusEnum, MapModeEnum } from 'enums';

import { isAnsvarigOrganisation, isAnsvarigOrganisationOrGranskare } from 'utilities/bebyggelseobjektUtilities';
import { isMobile } from 'utilities/isMobile';
import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

const InventeringPresentation: FC = () => {
  const dispatch = useAppDispatch();
  const inventering = useAppSelector((state: RootState) => state.aktivitet.data?.inventering);
  const { data: aktivitetDomaner } = useAppSelector((state: RootState) => state.aktivitetDomaner);
  const {
    data: user,
    selected_organisation,
    rolesInSelectedOrganisation
  } = useAppSelector((state: RootState) => state.user);

  const {
    requestStatus: utkastRequestStatus,
    error: utkastError,
    data: bebyggelseobjektUtkast
  } = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast);

  const {
    requestStatus: publiceradRequestStatus,
    data: publiceradeBebyggelseobjekt,
    error: publiceradError
  } = useAppSelector((state: RootState) => state.publiceradeBebyggelseobjekt);

  const [geometryFileName, setGeometryFileName] = useState<string>();

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.INVENTERING_PRESENTATION));
  }, [dispatch]);

  useEffect(() => {
    replaceUtkastFeaturesInActiveSource(bebyggelseobjektUtkast);
  }, [bebyggelseobjektUtkast]);

  useEffect(() => {
    replaceBebyggelseobjektFeaturesInActiveSource(publiceradeBebyggelseobjekt);
  }, [publiceradeBebyggelseobjekt]);

  useInitialFocusOnExtent(
    useCallback(
      () =>
        inventering?.inventeringsomrade &&
        replaceInventeringInActiveSource({
          featureCollection: inventering.inventeringsomrade,
          inventering: inventering
        }),
      [inventering]
    )
  );

  useEffect(() => {
    if (inventering) {
      setGeometryFileName(`${inventering.aktivitetsnummer} ${inventering.inventeringsomrade_datum}`);

      if (
        selected_organisation &&
        isAnsvarigOrganisationOrGranskare(inventering, selected_organisation, rolesInSelectedOrganisation)
      ) {
        dispatch(getBebyggelseobjektUtkast(inventering.aktivitet_id));
      }
    }
  }, [inventering, selected_organisation, rolesInSelectedOrganisation, dispatch]);

  const getKommunAndLanText = (geografiskIndelning: GeografiskIndelning): string =>
    geografiskIndelning.kommun.map((kommun) => `${kommun.kommun_namn} kommun, ${kommun.lan_namn} län`).join(', ');

  return (
    <>
      {inventering && (
        <DrawerBody>
          <DrawerSection>
            <ShareButton />
            <h3 className="tw-heading-lg tw-mb-6">{inventering.titel}</h3>
            <div className="tw-my-4 tw-flex tw-items-center tw-justify-between tw-text-center">
              <div className="tw-flex tw-items-center">
                <img
                  src={'data:image/svg+xml,' + encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE))}
                  alt="Ikon för inventering"
                />
                <p className="tw-ml-2">Inventering {inventering.aktivitetsnummer}</p>
              </div>
              <p className="raa-secondary-text tw-block tw-max-w-[50%] tw-items-center tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                {getKommunAndLanText(inventering.geografisk_indelning)}
              </p>
            </div>

            <p className="tw-mb-4">
              {
                aktivitetDomaner?.aktivitettyper.find(
                  (aktivitettyp) => aktivitettyp.aktivitettyp === inventering?.aktivitetstyp
                )?.beskrivning
              }
            </p>

            <table className="tw-mb-6 tw-w-full">
              <tbody className="tw-w-full">
                <tr>
                  <th className="tw-text-left">Status</th>
                  <td>
                    {aktivitetDomaner?.aktivitetstatustyper.find(
                      (typ: AktivitetStatusTyp) => typ.aktivitetstatustyp === inventering.aktivitetstatus.status
                    )?.namn || 'HITTADE INGEN STATUS'}
                  </td>
                </tr>
                <tr>
                  <th className="tw-text-left">Ansvarig organisation</th>
                  <td>{inventering.ansvarig_organisation.organisationsnamn}</td>
                </tr>
                <tr>
                  <th className="tw-text-left">Inventeringsår</th>
                  <td>
                    {inventering.start_ar}
                    {inventering.slut_ar && `–${inventering.slut_ar}`}
                  </td>
                </tr>
                <tr>
                  <th className="tw-text-left">Inventeringsyta</th>
                  <td className="tw-flex tw-gap-2">
                    <CenterActiveSourceGeometryButton />
                    {inventering.inventeringsomrade && geometryFileName && (
                      <GeometryFileDownload fileName={geometryFileName} geometry={inventering.inventeringsomrade} />
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <h3 className="tw-heading-md tw-mb-4">Syfte och metod</h3>
            <RaaExpandablePreview ariaLabel="syfte">
              <div className="tw-mb-4">{renderWithParagraphs(inventering.syfte)}</div>
            </RaaExpandablePreview>
          </DrawerSection>
          {isAnsvarigOrganisationOrGranskare(inventering, selected_organisation, rolesInSelectedOrganisation) && (
            <DrawerSection>
              <h3 className="tw-heading-md tw-mb-6">
                Utkast för inventerade objekt ({bebyggelseobjektUtkast ? bebyggelseobjektUtkast.length : 0})
              </h3>
              {(!inventering || inventering?.aktivitetstatus.status === AktivitetStatusEnum.PLANERAD) && (
                <p>Spara inventeringen som &rdquo;Pågående&rdquo; för att kunna lägga till bebyggelseobjekt.</p>
              )}
              {isAnsvarigOrganisation(inventering, selected_organisation) &&
                inventering?.aktivitetstatus.status === AktivitetStatusEnum.PAGAENDE && (
                  <TextButton
                    icon="raa-icon-add"
                    disabled={!inventering}
                    onClick={() => {
                      dispatch(setMapMode(MapModeEnum.ADD_UTKAST_TO_INVENTERING));

                      if (isMobile()) {
                        dispatch(setDrawerOpen('minimized'));
                      }
                    }}
                  >
                    Lägg till bebyggelseobjekt
                  </TextButton>
                )}

              <UtkastList
                list={bebyggelseobjektUtkast}
                error={utkastError}
                user={user}
                requestStatus={utkastRequestStatus}
                action={(utkast: BebyggelseobjektUtkastIndex): ReactElement => (
                  <GetUtkastAction utkast={utkast} aktivitet={inventering} />
                )}
              />
            </DrawerSection>
          )}

          <DrawerSection>
            <h3 className="tw-heading-md tw-mb-6">
              Inventerade objekt ({publiceradeBebyggelseobjekt ? publiceradeBebyggelseobjekt.length : 0})
            </h3>
            <PubliceradeBebyggelseobjekt
              list={publiceradeBebyggelseobjekt}
              error={publiceradError}
              requestStatus={publiceradRequestStatus}
            />
          </DrawerSection>
        </DrawerBody>
      )}
    </>
  );
};

export default InventeringPresentation;
